"use client";

import React from 'react';
import { Check, Maximize2 } from "lucide-react";

export interface SpecsBentoBlockProps {
    subtitle?: string;
    title?: string;
    description?: string;
    
    // Spec 1
    spec1Tag?: string;
    spec1Category?: string;
    spec1Title?: string;
    spec1Desc?: string;
    spec1ConsoleInput?: string;
    spec1ConsoleStatus?: string;
    spec1ConsoleOutput?: string;
    
    // Spec 2
    spec2Tag?: string;
    spec2Category?: string;
    spec2Title?: string;
    spec2Desc?: string;
    spec2MetricLabel?: string;
    
    style?: {
        backgroundColor?: string;
        textColor?: string;
        accentColor?: string;
        paddingTop?: string;
        paddingBottom?: string;
    };
}

export default function SpecsBentoBlock({
    subtitle = "// System Engineering Specs",
    title = "Design System Architecture",
    description = "How we code luxury details to perform at scale.",
    spec1Tag = "Spec 01",
    spec1Category = "Architecture",
    spec1Title = "Custom Endpoint Handshake",
    spec1Desc = "We architected the site using clean, semantic directories: `/rooms`, `/staples` (dining), `/wellness` (spa & pool), and `/halls` (banquets). Custom APIs handle room availability check-in requests directly, bypassing heavy third-party iframe overlays.",
    spec1ConsoleInput = "GET /api/check-availability?room=penthouse",
    spec1ConsoleStatus = "[System] Validating dates...",
    spec1ConsoleOutput = "[Secure Handshake Verified] 200 OK",
    spec2Tag = "Spec 02",
    spec2Category = "Media UX",
    spec2Title = "AVIF Compression Pipeline",
    spec2Desc = "Since hospitality relies heavily on photos, we compressed high-res imagery into next-gen AVIF containers, dropping loading speeds to sub-100ms.",
    spec2MetricLabel = "128kb average payload per image",
    style = {}
}: SpecsBentoBlockProps) {
    const bg = style.backgroundColor || "#0B0B0C";
    const text = style.textColor || "#FAF7F2";
    const accent = style.accentColor || "#c5a880";

    return (
        <div 
            className="w-full px-6 md:px-12 lg:px-24"
            style={{
                backgroundColor: bg,
                color: text,
                paddingTop: style.paddingTop || "6rem",
                paddingBottom: style.paddingBottom || "6rem"
            }}
        >
            <div className="container mx-auto">
                <div className="max-w-2xl mb-16">
                    {subtitle && (
                        <span className="text-xs font-mono uppercase tracking-widest block mb-4" style={{ color: accent }}>
                            {subtitle}
                        </span>
                    )}
                    {title && (
                        <h2 className="text-4xl md:text-5xl font-light leading-tight font-cormorant">
                            {title}
                        </h2>
                    )}
                    {description && (
                        <p className="text-zinc-500 text-sm mt-2 font-sans font-light">
                            {description}
                        </p>
                    )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    
                    {/* Spec 1: Modular Portal Routing */}
                    <div className="md:col-span-2 bg-[#121214] border border-zinc-850 rounded-2xl p-8 flex flex-col justify-between relative overflow-hidden group hover:border-zinc-700 transition-all duration-500">
                        <div className="absolute top-0 right-0 w-48 h-48 rounded-full blur-[60px] pointer-events-none"
                             style={{ backgroundColor: `${accent}0c` }} />
                        
                        <div className="space-y-4">
                            <div className="inline-flex items-center gap-2">
                                {spec1Tag && (
                                    <span className="text-[10px] font-mono uppercase border px-2 py-0.5 rounded"
                                          style={{ backgroundColor: `${accent}26`, color: accent, borderColor: `${accent}33` }}>
                                        {spec1Tag}
                                    </span>
                                )}
                                {spec1Category && <span className="text-[10px] font-mono text-zinc-500 uppercase">{spec1Category}</span>}
                            </div>
                            {spec1Title && <h3 className="text-2xl font-normal text-white font-cormorant">{spec1Title}</h3>}
                            {spec1Desc && <p className="text-zinc-400 text-xs md:text-sm leading-relaxed max-w-lg font-light font-sans">{spec1Desc}</p>}
                        </div>

                        {(spec1ConsoleInput || spec1ConsoleStatus || spec1ConsoleOutput) && (
                            <div className="mt-8 bg-zinc-950 border border-zinc-900 rounded-lg p-4 font-mono text-[10px] text-zinc-450 space-y-1.5 shadow-inner">
                                {spec1ConsoleInput && <div style={{ color: accent }}>&gt; {spec1ConsoleInput}</div>}
                                {spec1ConsoleStatus && <div className="text-zinc-500">{spec1ConsoleStatus}</div>}
                                {spec1ConsoleOutput && (
                                    <div className="text-emerald-400 flex items-center gap-1">
                                        <Check className="w-3.5 h-3.5" /> {spec1ConsoleOutput}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Spec 2: Media Performance */}
                    <div className="bg-[#121214] border border-zinc-850 rounded-2xl p-8 flex flex-col justify-between relative overflow-hidden group hover:border-zinc-700 transition-all duration-500">
                        <div className="space-y-4">
                            <div className="inline-flex items-center gap-2">
                                {spec2Tag && (
                                    <span className="text-[10px] font-mono uppercase border px-2 py-0.5 rounded"
                                          style={{ backgroundColor: `${accent}26`, color: accent, borderColor: `${accent}33` }}>
                                        {spec2Tag}
                                    </span>
                                )}
                                {spec2Category && <span className="text-[10px] font-mono text-zinc-500 uppercase">{spec2Category}</span>}
                            </div>
                            {spec2Title && <h3 className="text-2xl font-normal text-white font-cormorant">{spec2Title}</h3>}
                            {spec2Desc && <p className="text-zinc-400 text-xs leading-relaxed font-light font-sans">{spec2Desc}</p>}
                        </div>

                        {spec2MetricLabel && (
                            <div className="mt-6 flex items-center gap-2 text-xs font-mono text-zinc-400">
                                <div className="w-8 h-8 rounded-full bg-zinc-950 flex items-center justify-center border border-zinc-900">
                                    <Maximize2 className="w-4 h-4" style={{ color: accent }} />
                                </div>
                                <span>{spec2MetricLabel}</span>
                            </div>
                        )}
                    </div>

                </div>
            </div>
        </div>
    );
}
